import { MultipleSelect } from '@WebComponents/MultipleSelect';
import { Select } from '@WebComponents/Select';
import { Toggle } from '@WebComponents/Toggle';
import { css, StyleDeclaration } from 'aphrodite';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { SimpleAutoCompleteSearchField } from '../../../../../../../web/components/autocomplete/SimpleAutoCompleteSearchField';
import { Button } from '../../../../../../../web/components/Button';
import { styleSheet } from './styles';

interface CustomStylesProps {
	customStyles?: StyleDeclaration[];
	customInlineStyles?: React.CSSProperties;
}

interface EditModeContextType {
	isEditing: boolean;
}

export const EditModeContext = createContext<EditModeContextType>({ isEditing: false });

export const EditModeProvider: React.FC<{
	isEditing: boolean;
	children: React.ReactNode;
}> = ({ isEditing, children }) => {
	return <EditModeContext.Provider value={{ isEditing }}>{children}</EditModeContext.Provider>;
};

export const useEditMode = () => useContext(EditModeContext);

export const EditableMultipleSelect = (
	props: React.ComponentProps<typeof MultipleSelect> & { displayValue: string } & CustomStylesProps
) => {
	const { isEditing } = useEditMode();
	const containerRef = useRef<HTMLDivElement>(null);
	const [isOverflowing, setIsOverflowing] = useState(false);

	useEffect(() => {
		// Check if text is overflowing
		const checkOverflow = () => {
			const element = containerRef.current;
			if (element) {
				// Use setTimeout to ensure DOM is fully rendered
				setTimeout(() => {
					const isOverflow = element.scrollWidth > element.clientWidth || element.offsetWidth < element.scrollWidth;
					setIsOverflowing(isOverflow);
				}, 0);
			}
		};

		checkOverflow();
		// Also check on window resize
		window.addEventListener('resize', checkOverflow);
		return () => window.removeEventListener('resize', checkOverflow);
	}, [props.displayValue]);

	if (!isEditing) {
		// Display toggle value as text when not in edit mode
		const { customStyles: propsCustomStyles = [], customInlineStyles: propsInlineStyles } = props;

		return (
			<div
				ref={containerRef}
				className={css(
					styleSheet.readOnlyField,
					isOverflowing && styleSheet.readOnlyFieldOverflow,
					...propsCustomStyles
				)}
				style={propsInlineStyles}
				title={isOverflowing ? props.displayValue : undefined}
			>
				{props.displayValue}
			</div>
		);
	}

	const { displayValue, customStyles, customInlineStyles, ...multiSelectProps } = props;
	return <MultipleSelect {...multiSelectProps} />;
};

export const EditableToggle = (
	props: React.ComponentProps<typeof Toggle> & { displayValue?: string } & CustomStylesProps
) => {
	const { isEditing } = useEditMode();

	if (!isEditing) {
		// Display toggle value as text when not in edit mode
		const { customStyles: propsCustomStyles = [], customInlineStyles: propsInlineStyles } = props;
		return (
			<div className={css(styleSheet.readOnlyField, ...propsCustomStyles)} style={propsInlineStyles}>
				{props.displayValue ? props.displayValue : props.isOn ? 'Enabled' : 'Disabled'}
			</div>
		);
	}

	const { customStyles, customInlineStyles, ...toggleProps } = props;
	return <Toggle {...toggleProps} />;
};

export function EditableSelect<Value>(
	props: React.ComponentProps<typeof Select<Value>> & { displayValue: string } & CustomStylesProps
) {
	const { isEditing } = useEditMode();

	if (!isEditing) {
		const { customStyles: propsCustomStyles = [], customInlineStyles: propsInlineStyles } = props;
		return (
			<div className={css(styleSheet.readOnlyField, ...propsCustomStyles)} style={propsInlineStyles}>
				{props.displayValue}
			</div>
		);
	}

	const { displayValue, customStyles, customInlineStyles, ...selectProps } = props;
	return <Select<Value> {...selectProps} />;
}

export const EditableSimpleAutoCompleteSearchField = (
	props: React.ComponentProps<typeof SimpleAutoCompleteSearchField> & {
		displayValue: string;
		textAlign?: React.CSSProperties['textAlign'];
	} & CustomStylesProps
) => {
	const { isEditing } = useEditMode();

	if (!isEditing) {
		const { customStyles: propsCustomStyles = [], customInlineStyles: propsInlineStyles } = props;

		return (
			<div className={css(styleSheet.readOnlyField, ...propsCustomStyles)} style={propsInlineStyles}>
				{props.displayValue}
			</div>
		);
	}

	const { displayValue, customStyles, customInlineStyles, textAlign, ...searchFieldProps } = props;
	return <SimpleAutoCompleteSearchField {...searchFieldProps} />;
};

export const EditableButton = (
	props: React.ComponentProps<typeof Button> & { displayValue: string } & CustomStylesProps
) => {
	const { isEditing } = useEditMode();
	const { customStyles, customInlineStyles, displayValue, ...buttonProps } = props;

	return (
		<Button disabled={!isEditing} {...buttonProps}>
			{displayValue}
		</Button>
	);
};
